import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type ShellHeader = { title: string; subtitle?: string };

type ShellHeaderState = {
  title: string;
  subtitle: string;
  setHeader: (next: ShellHeader) => void;
  clearHeader: () => void;
};

const ShellHeaderContext = createContext<ShellHeaderState | null>(null);

type ShellHeaderProviderProps = {
  children: ReactNode;
  fallbackTitle: string;
  fallbackSubtitle: string;
};

export function ShellHeaderProvider({
  children,
  fallbackTitle,
  fallbackSubtitle,
}: ShellHeaderProviderProps) {
  const [override, setOverride] = useState<ShellHeader | null>(null);

  const setHeader = useCallback((next: ShellHeader) => {
    setOverride(next);
  }, []);

  const clearHeader = useCallback(() => {
    setOverride(null);
  }, []);

  const value = useMemo(
    () => ({
      title: override?.title || fallbackTitle,
      subtitle: override?.subtitle || fallbackSubtitle,
      setHeader,
      clearHeader,
    }),
    [override, fallbackTitle, fallbackSubtitle, setHeader, clearHeader]
  );

  return <ShellHeaderContext.Provider value={value}>{children}</ShellHeaderContext.Provider>;
}

export function useShellHeaderState(): ShellHeaderState {
  const context = useContext(ShellHeaderContext);
  if (!context) {
    throw new Error('useShellHeaderState must be used within ShellHeaderProvider');
  }
  return context;
}

export function useSetShellHeader({ title, subtitle }: ShellHeader): void {
  const { setHeader, clearHeader } = useShellHeaderState();

  useEffect(() => {
    setHeader({ title, subtitle });
    return clearHeader;
  }, [title, subtitle, setHeader, clearHeader]);
}
